// FILE: components/study/PathFullscreen.tsx
"use client";

import { useCallback, useEffect, useState, lazy, Suspense } from "react";
import type { ResumeFromDraft } from "./StudyFullscreenShell";
import StudyFullscreenShell, { type SubmitPayload } from "./StudyFullscreenShell";
import { type StudyQuestion, type StudyMode } from "./studyData";
import { buildQuestionAccessMap } from "@/lib/path-access";
import { isRealQuestionId } from "@/lib/study-items";
import { useDiamonds } from "@/lib/DiamondsContext";
import PurchaseConfirmModal from "@/components/shared/PurchaseConfirmModal";
import ReviewReaderCard from "@/components/paths/ReviewReaderCard";
import LessonInline from "@/components/lesson/LessonInline";

const LessonFullscreen = lazy(() => import("@/components/lesson/LessonFullscreen"));

interface Props {
  onExit?: () => void;
  pathId?: string | null;
}

export default function PathFullscreen({ onExit, pathId }: Props) {
  const { diamonds } = useDiamonds();

  // ── Load draft từ DB (đồng bộ mọi thiết bị) ──
  const [draft, setDraft] = useState<ResumeFromDraft | null>(null);
  const [draftReady, setDraftReady] = useState(false);

  useEffect(() => {
    if (!pathId) { setDraftReady(true); return; }
    let alive = true;
    (async () => {
      try {
        const [{ getPathDraft }, progRes] = await Promise.all([
          import("@/actions/study"),
          fetch(`/api/mindmap/progress?pathId=${pathId}`).then(r => r.json()).catch(() => ({ progress: [] })),
        ]);
        const d = await getPathDraft(pathId);

        // Đáp án lấy từ mindmap_question_progress (nguồn chung cho cả tab Mindmap và tab Học)
        const fromProgress = (progRes?.progress ?? [])
          .map((p: any) => ({ question_id: p.question_id, selected: p.selected ?? null }))
          .filter((a: any) => a.selected);

        if (alive && (d || fromProgress.length > 0)) {
          setDraft({
            current_index: d?.current_index ?? 0,
            answers: fromProgress.length > 0 ? fromProgress : (d?.answers ?? []),
            time_spent: d?.time_spent ?? 0,
          });
        }
      } catch {}
      if (alive) setDraftReady(true);
    })();
    return () => { alive = false; };
  }, [pathId]);

  const onSaveDraft = useCallback(async (data: {
    currentIndex: number;
    answers: Array<{ question_id: string; selected: string | null; status?: string }>;
    timeSpent: number;
  }) => {
    if (!pathId) return;

    // 1. Đáp án -> mindmap_question_progress (nguồn duy nhất, dùng chung với tab Mindmap)
    const graded = data.answers.filter(a => a.status === "correct" || a.status === "wrong");
    if (graded.length > 0) {
      await fetch("/api/mindmap/progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: graded }),
      }).catch(() => {});
    }

    // 2. Vị trí đang dở -> path_drafts (mindmap không có khái niệm này)
    const { savePathDraft } = await import("@/actions/study");
    await savePathDraft(pathId, {
      current_index: data.currentIndex,
      answers: data.answers,
      time_spent: data.timeSpent,
    });
  }, [pathId]);

  const onDeleteDraft = useCallback(async () => {
    if (!pathId) return;
    const { deletePathDraft } = await import("@/actions/study");
    await deletePathDraft(pathId);
  }, [pathId]);

  const fetchMeta = useCallback(async () => {
    if (!pathId) return null;
    const { getPathStudyMeta } = await import("@/actions/study");
    return getPathStudyMeta(pathId);
  }, [pathId]);

  const fetchChunk = useCallback(async (ids: string[]) => {
    if (!pathId) return [];
    const { getPathStudyChunk } = await import("@/actions/study");
    return getPathStudyChunk(pathId, ids);
  }, [pathId]);

  const onSaveProgress = useCallback(async (pct: number) => {
    if (!pathId) return;
    const { savePathProgress } = await import("@/actions/study");
    await savePathProgress(pathId, pct);
  }, [pathId]);

  const onSubmitToServer = useCallback(async (payload: SubmitPayload) => {
    if (!pathId) return;
    await fetch("/api/path-submissions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pathId, ...payload }),
    });
  }, [pathId]);

  // Render lesson/review thay cho QuestionCard mặc định
  const renderSpecialQuestion = useCallback((
    q: StudyQuestion,
    idx: number,
    helpers: {
      setQuestions: React.Dispatch<React.SetStateAction<StudyQuestion[]>>;
      openLesson: (id: string) => void;
    }
  ) => {
    if (q.isReview) {
      return (
        <ReviewReaderCard
          index={idx}
          review={{
            id: q.reviewSlug ?? q.id,
            title: q.text.replace(/^\p{Emoji}\p{EMod}*\s*/u, "").trim(),
            emoji: q.text.match(/^(\p{Emoji}\p{EMod}*)/u)?.[1] ?? "📖",
            content: q.reviewContent ?? q.passage ?? "",
          }}
          isRead={q.status === "correct"}
          onMarkRead={() => {
            helpers.setQuestions(prev => prev.map(qq =>
              qq.id === q.id ? { ...qq, status: "correct" as const } : qq
            ));
          }}
        />
      );
    }
    if (q.isLesson && q.lesson && q.lessonUrls) {
      return (
        <LessonInline
          lesson={q.lesson}
          urls={q.lessonUrls}
          onFullscreen={() => helpers.openLesson(q.lessonId!)}
          onCollapse={() => { /* no-op trong study player */ }}
        />
      );
    }
    return null; // → Shell render QuestionCard mặc định
  }, []);

  // Path: filter bỏ lesson khi exam mode
  const filterVisibleQuestions = useCallback((questions: StudyQuestion[], mode: StudyMode) => {
    return mode === "exam" ? questions.filter(q => isRealQuestionId(q.id) && !q.isLesson && !q.isReview) : questions;
  }, []);

  // Path: lesson fullscreen modal
  const renderLessonFullscreen = useCallback((
    lessonId: string,
    questions: StudyQuestion[],
    onClose: () => void
  ) => {
    const item = questions.find(q => q.lessonId === lessonId);
    if (!item?.isLesson || !item.lesson || !item.lessonUrls) return null;
    return (
      <Suspense fallback={null}>
        <LessonFullscreen
          lesson={item.lesson}
          urls={item.lessonUrls}
          onMinimize={onClose}
          onClose={onClose}
        />
      </Suspense>
    );
  }, []);

  if (!draftReady) return null;

  return (
    <StudyFullscreenShell
      kind="path"
      itemId={pathId ?? null}
      onExit={onExit}
      fetchMeta={fetchMeta}
      fetchChunk={fetchChunk}
      buildAccessMap={buildQuestionAccessMap}
      mindmapProps={{ pathId }}
      editorUrl={`/studio/paths/${pathId}`}
      onSaveProgress={onSaveProgress}
      onSaveDraft={onSaveDraft}
      onDeleteDraft={onDeleteDraft}
      resumeFromDraft={draft}
      onSubmitToServer={onSubmitToServer}
      renderSpecialQuestion={renderSpecialQuestion}
      renderLessonFullscreen={renderLessonFullscreen}
      filterVisibleQuestions={filterVisibleQuestions}
      PurchaseConfirmModal={PurchaseConfirmModal}
      userBalance={diamonds}
    />
  );
}